export interface BucketRow { ts: string; rate: number; baseline: number; is_spike: boolean }
export interface LiveRow   { ts: string; platform: string; is_live: boolean; url?: string }
export interface UploadRow {
  ts: string;
  platform: string;
  url?: string;
  title?: string;
  ok?: boolean;
  error?: string;
  edited_basename?: string;
}
export interface StreamerRec {
  name: string;
  platforms: Record<string, { channel?: string; channel_id?: string }>;
  chat: { bucket_seconds: number; baseline_window_minutes: number; spike_threshold_x: number; pre_roll_seconds: number; post_roll_seconds: number };
  edit_prompt?: string;
  upload_targets?: Record<string, unknown>;
}
export interface StreamerState {
  streamer: StreamerRec;
  buckets: BucketRow[];
  uploads: UploadRow[];
  live: LiveRow[];
}
export interface ClipFile { name: string; size: number; mtime: number }
export interface ClipsList { raw: ClipFile[]; edited: ClipFile[] }

function operatorHeaders(): Record<string, string> {
  const key = (import.meta.env.VITE_OPERATOR_KEY as string | undefined)
    ?? (typeof localStorage !== 'undefined' ? (localStorage.getItem('operator_key') ?? undefined) : undefined);
  return key ? { 'x-operator-key': key } : {};
}

async function jsonFetch<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  for (const [k, v] of Object.entries(operatorHeaders())) headers.set(k, v);
  const res = await fetch(input, { ...init, headers });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    if (res.status === 402) {
      // Surface the x402 challenge verbatim; UI decides whether to prompt or fail.
      throw new Error(`402 Payment Required: ${text.slice(0, 240)}`);
    }
    throw new Error(`${res.status} ${res.statusText}: ${text.slice(0, 200)}`);
  }
  return (await res.json()) as T;
}

export const api = {
  listStreamers: () => jsonFetch<{ streamers: StreamerRec[] }>('/api/streamers'),

  addStreamer: (payload: Record<string, unknown>) =>
    jsonFetch<StreamerRec>('/api/streamers', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload)
    }),

  getStreamer: (name: string) =>
    jsonFetch<StreamerState>(`/api/streamers/${encodeURIComponent(name)}`),

  deleteStreamer: (name: string) =>
    jsonFetch<{ deleted: boolean }>(`/api/streamers/${encodeURIComponent(name)}`, { method: 'DELETE' }),

  probe: (name: string) =>
    jsonFetch<unknown>(`/api/streamers/${encodeURIComponent(name)}/probe`, { method: 'POST' }),

  startMonitor: (name: string) =>
    jsonFetch<{ started?: boolean; reason?: string }>(
      `/api/streamers/${encodeURIComponent(name)}/monitor`,
      { method: 'POST' }
    ),

  stopMonitor: (name: string) =>
    jsonFetch<{ stopped?: boolean }>(`/api/streamers/${encodeURIComponent(name)}/monitor`, {
      method: 'DELETE'
    }),

  monitorStatus: (name?: string) =>
    jsonFetch<Record<string, unknown> | null>(
      `/api/monitoring${name ? `?name=${encodeURIComponent(name)}` : ''}`
    ),

  listClips: (name: string) =>
    jsonFetch<ClipsList>(`/api/streamers/${encodeURIComponent(name)}/clips`),

  setEditPrompt: (name: string, prompt: string) =>
    jsonFetch<{ ok: boolean }>(`/api/streamers/${encodeURIComponent(name)}/edit-prompt`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ prompt })
    }),

  triggerEdit: (name: string, raw_basename: string) =>
    jsonFetch<{ ok: boolean; out?: string; error?: string }>(
      `/api/streamers/${encodeURIComponent(name)}/clips`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ action: 'edit', raw_basename })
      }
    ),

  triggerUpload: (
    name: string,
    edited_basename: string,
    only?: 'youtube' | 'tiktok' | 'instagram'
  ) =>
    jsonFetch<{ rows: UploadRow[] }>(
      `/api/streamers/${encodeURIComponent(name)}/clips`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ action: 'upload', edited_basename, only })
      }
    )
};
